/**
 * Раздел недоступен организации: тариф ниже Pro или раздел выключен (#848).
 * Словами, а не ошибкой «попробуйте снова» — повтор здесь ничего не изменит.
 * Тариф ниже Pro (`tier_not_eligible`) — общий экран тарифа (#1897): план, что
 * даёт раздел, тарифы или владелец аккаунта; свои слова остались у выключенного
 * раздела и у оболочки, которая не отличает тариф от выключателя.
 */
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { VOICE_OF_CUSTOMER_FLAG_KEY } from '@aica/core';
import type { TierFeatureRefusal } from '@aica/api-types/schemas';
import type { FeatureGateState } from '@/contexts/FeatureFlagsContext';
import { TierLockedScreen } from '@/components/ui/TierLockedScreen';

/**
 * Whether the section is open for this organization, as the shell learned it —
 * the flag map's own vocabulary (`FeatureGateState`: `loading`, `enabled`, the
 * server's refusal code, `failed` — the check itself failed, neither yes nor
 * no, #1379) plus `unavailable`: a shell that knows only "no" (the Bitrix24
 * session feature) and cannot tell the plan from a switch.
 */
export type VoiceSectionAccess = FeatureGateState | 'unavailable';

interface VoiceLockedProps {
  /**
   * The server's refusal, or `unavailable` — the shell's own flag says no and
   * cannot tell a plan below Pro from a section switched off.
   */
  code: TierFeatureRefusal | 'unavailable';
  /**
   * Where the plans live for `unavailable` — only for a role that can open
   * billing; absent otherwise (and in Bitrix24). A plan below Pro does not read
   * it: the tier screen decides the link by the role itself.
   */
  upgradeHref?: string;
}

export function VoiceLocked({ code, upgradeHref }: VoiceLockedProps) {
  const { t } = useTranslation('dashboard');
  if (code === 'tier_not_eligible') {
    return <TierLockedScreen flag={VOICE_OF_CUSTOMER_FLAG_KEY} />;
  }
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-border bg-bg-surface px-4 py-8 text-center">
      <Lock className="h-5 w-5 text-fg-muted" />
      <p className="text-sm font-medium text-fg">{t(`voicePage.locked.${code}.title`)}</p>
      <p className="max-w-prose text-sm text-fg-muted">
        {t(`voicePage.locked.${code}.body`)}
      </p>
      {code === 'unavailable' && upgradeHref && (
        <a
          href={upgradeHref}
          className="text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('voicePage.locked.tier_not_eligible.cta')}
        </a>
      )}
    </div>
  );
}
